'use client';

import { X, Heart, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import { useWishlist } from '@/lib/wishlist-store';
import { useCart } from '@/lib/cart-store';

export default function WishlistDrawer() {
  const { items, isOpen, closeWishlist, removeItem, clear } = useWishlist();
  const { addItem, openCart } = useCart();

  const handleAddToCart = (item: typeof items[0]) => {
    addItem({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image,
      category: item.category,
    });
    removeItem(item.id);
  };

  const handleAddAllToCart = () => {
    items.forEach((item) =>
      addItem({
        id: item.id,
        name: item.name,
        price: item.price,
        image: item.image,
        category: item.category,
      })
    );
    clear();
    closeWishlist();
    openCart();
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeWishlist}
      />

      <aside
        className={`fixed top-0 right-0 bottom-0 z-[95] w-full sm:w-[440px] bg-[#0a0a0f] border-l border-[#D4AF37]/25 shadow-2xl transition-transform duration-400 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#D4AF37]/20">
          <div className="flex items-center gap-3">
            <Heart size={20} className="text-[#D4AF37] fill-[#D4AF37]" />
            <div>
              <div className="font-display text-base font-semibold text-[#F4E8C1] tracking-wide uppercase">
                Mes Favoris
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80">
                {items.length} {items.length === 1 ? 'article' : 'articles'}
              </div>
            </div>
          </div>
          <button
            onClick={closeWishlist}
            className="w-9 h-9 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-all"
            aria-label="Fermer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Items */}
        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
            <Heart size={48} className="text-[#D4AF37]/30 mb-5" />
            <p className="font-serif italic text-lg text-[#F4E8C1]/60 mb-2">
              Vos favoris sont vides
            </p>
            <p className="text-xs text-[#F4E8C1]/40 max-w-xs leading-relaxed">
              Cliquez sur le cœur d'un produit pour l'ajouter ici. Votre
              sélection vous attend pour passer commande.
            </p>
            <button
              onClick={closeWishlist}
              className="mt-7 px-7 py-3 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.18em] text-xs hover:glow-gold transition-all"
            >
              Découvrir la boutique
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex gap-4 p-4 border-b border-[#D4AF37]/12 hover:bg-[#12121a]/50 transition-colors"
              >
                <div className="flex-shrink-0 w-20 h-24 overflow-hidden border border-[#D4AF37]/20">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    style={{ filter: 'brightness(0.92) saturate(1.05)' }}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/85 mb-1">
                    {item.category}
                  </div>
                  <h4 className="font-display text-sm font-semibold text-[#F4E8C1] mb-1 leading-tight">
                    {item.name}
                  </h4>
                  <div className="font-display text-sm text-gold-gradient font-bold mb-3">
                    {item.price.toLocaleString('fr-BE')} €
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleAddToCart(item)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gold-gradient text-[#0a0a0f] text-[9px] uppercase tracking-[0.18em] font-semibold hover:glow-gold transition-all"
                    >
                      <ShoppingBag size={11} />
                      Ajouter au panier
                    </button>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-[#F4E8C1]/40 hover:text-[#CE1126] transition-colors p-1.5"
                      aria-label="Retirer des favoris"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-[#D4AF37]/20 p-5 bg-[#070709]">
            <button
              onClick={handleAddAllToCart}
              className="w-full inline-flex items-center justify-center gap-3 py-4 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.2em] text-xs hover:glow-gold-strong transition-all duration-400"
            >
              <ShoppingBag size={14} />
              Tout ajouter au panier
              <ArrowRight size={12} />
            </button>
            <button
              onClick={clear}
              className="w-full mt-2 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/55 hover:text-[#CE1126] transition-colors"
            >
              Vider mes favoris
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
